import { CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { AuthService } from '../services/account/auth.service';
import { UserRole } from '../models/enums/UserRole';

export interface DecodedToken extends JwtPayload {
  [key: string]: any;
}

const getStoredAccount = () => {
  return JSON.parse(localStorage.getItem('account') || 'null');
};
const isTokenExpired = (expiration: string) => new Date(expiration) < new Date();

const redirectToSignIn = (router: Router, expectedRoles: string[]) => {
  localStorage.removeItem('account');
  const roleRedirects: Record<string, string> = {
    SuperAdmin: '/partner-signin',
    Admin: '/partner-signin',
    Mentor: '/partner-signin',
    PeerReviewer: '/partner-signin',
    SchoolAdmin: '/partner-signin',
    Operations: '/partner-signin',
  };
  const redirectPath =
    expectedRoles.map((role) => roleRedirects[role]).find(Boolean) || '/signin';
  router.navigate([redirectPath]);
};

const rolesFromAccountToken = (): string[] => {
  const ac = getStoredAccount();
  if (!ac?.token) return [];
  try {
    const d = jwtDecode<DecodedToken>(ac.token);
    const raw = d['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    if (raw == null) return [];
    return Array.isArray(raw) ? raw : [raw];
  } catch {
    return [];
  }
};

const redirectToPortal = (router: Router, expectedRoles: string[]) => {
  const roleRedirects: Record<string, string> = {
    SuperAdmin: '/admin',
    Admin: '/admin',
    Mentor: '/mentor-portal',
    PeerReviewer: '/peer-portal',
    SchoolAdmin: '/school-portal',
    Operations: '/operations-portal',
    Student: '/portal',
  };
  const effectiveRoles = expectedRoles.length ? expectedRoles : rolesFromAccountToken();
  const redirectPath =
    effectiveRoles.map((role) => roleRedirects[role]).find(Boolean) || '/portal';
  router.navigate([redirectPath]);
};

export const PermissionGuard: CanActivateFn = (route, state) => {

  const router = inject(Router);
  const authService = inject(AuthService);
  const expectedRoles: string[] = route.data['expectedRoles']??[];
  const account = getStoredAccount();

  const pathOnly = (state.url.split('?')[0] || '').toLowerCase();
  const isSignInLanding =
    /^\/(signin|partner-signin)\/?$/i.test(
      pathOnly
    );
  if (isSignInLanding) {
    if (!account || !account.token || !account.expiration) {
      return of(true);
    }
    else{
      redirectToPortal(router, expectedRoles);
      return of(false);
    }
  }

  if(account === null) {
    redirectToSignIn(router, expectedRoles);
    return of(false);
  }


  const { token, refreshToken, expiration, userId } = account;

  if (isTokenExpired(expiration)) {
    return from(authService.getJWTbyRefreshToken({ refreshToken, userId })).pipe(
      switchMap((response: any) => {
        if (!response?.token) {
          redirectToSignIn(router, expectedRoles);
          return of(false);
        }

        const decodedToken: DecodedToken = jwtDecode(response.token);
        const raw = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        const roles: string[] = raw == null ? [] : Array.isArray(raw) ? raw : [raw];
        const allowed =
          expectedRoles.length === 0 || expectedRoles.some((role) => roles.includes(role));
        if (!allowed) {
          redirectToSignIn(router, expectedRoles);
          return of(false);
        }
        localStorage.setItem('account', JSON.stringify({
          ...account,
          token: response.token,
          expiration: response.expiration,
        }));
        redirectToPortal(router, expectedRoles);
        return of(false);
      }),
      catchError(() => {
        redirectToSignIn(router, expectedRoles);
        return of(false);
      })
    );
  }

  try {
    const decodedToken: DecodedToken = jwtDecode(token);
    const raw = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    const roles: string[] = raw == null ? [] : Array.isArray(raw) ? raw : [raw];
    const hasRequiredRoles =
      expectedRoles.length === 0 || expectedRoles.some((role) => roles.includes(role));

    if (!hasRequiredRoles) {
      redirectToSignIn(router, expectedRoles);
      return of(false);
    }
    return of(true);
  } catch (error) {
    console.error('Error decoding token:', error);
    redirectToSignIn(router, expectedRoles);
    return of(false);
  }
};
